import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDButton from "shared/components/MDButton";
import { getAllCommerce } from "modules/bitacora/services/Configuracion/Comercio/comercioService";
import useFormDevice from "../../../hooks/useFormDevice";
import { getAllZone } from "modules/bitacora/services/Configuracion/Zona/zonaService";

const FormDevice = ({
  onSubmit,
  afterSubmit = () => {},
  initialData = {},
  isEditMode = false,
  loading: externalLoading = false,
}) => {
  const formik = useFormDevice(onSubmit, afterSubmit, initialData, isEditMode);

  const [commerces, setCommerces] = useState([]);
  const [zones, setZones] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [errorData, setErrorData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        // Traemos comercios y zonas
        const [commercesData, zonesData] = await Promise.all([getAllCommerce(), getAllZone()]);

        if (isMounted) {
          setCommerces(Array.isArray(commercesData) ? commercesData : []);
          setZones(Array.isArray(zonesData) ? zonesData : []);
        }
      } catch (error) {
        if (isMounted) {
          setErrorData("Error al cargar datos");
          console.error("Error loading data:", error);
        }
      } finally {
        if (isMounted) {
          setLoadingData(false);
        }
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (formik.values.commerceId) {
      const filtered = zones.filter(
        (zone) => Number(zone.commerceId) === Number(formik.values.commerceId)
      );
      setFilteredZones(filtered);
    } else {
      setFilteredZones([]);
    }
  }, [formik.values.commerceId, zones]);

  return (
    <Card>
      <MDBox p={3}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Comercio */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                error={formik.touched.commerceId && Boolean(formik.errors.commerceId)}
              >
                <InputLabel id="commerce-label">Comercio</InputLabel>
                <Select
                  label="Comercio"
                  labelId="commerce-label"
                  name="commerceId"
                  value={formik.values.commerceId || ""}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue("zoneId", ""); // reset zona
                  }}
                  onBlur={formik.handleBlur}
                  disabled={loadingData || formik.isSubmitting || externalLoading}
                  sx={{ height: 45 }}
                >
                  {loadingData ? (
                    <MenuItem disabled>Cargando comercios...</MenuItem>
                  ) : errorData ? (
                    <MenuItem disabled>{errorData}</MenuItem>
                  ) : (
                    commerces.map((commerce) => (
                      <MenuItem key={commerce.id} value={commerce.id}>
                        {commerce.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText>
                  {formik.touched.commerceId && formik.errors.commerceId}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Zona */}
            <Grid item xs={6}>
              <FormControl fullWidth error={formik.touched.zoneId && Boolean(formik.errors.zoneId)}>
                <InputLabel id="zone-label">Zona</InputLabel>
                <Select
                  label="Zona"
                  labelId="zone-label"
                  name="zoneId"
                  value={formik.values.zoneId || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={
                    loadingData ||
                    !formik.values.commerceId ||
                    formik.isSubmitting ||
                    externalLoading
                  }
                  sx={{ height: 45 }}
                >
                  {!formik.values.commerceId ? (
                    <MenuItem disabled>Seleccione un comercio primero</MenuItem>
                  ) : filteredZones.length === 0 ? (
                    <MenuItem disabled>No hay zonas para este comercio</MenuItem>
                  ) : (
                    filteredZones.map((zone) => (
                      <MenuItem key={zone.id} value={zone.id}>
                        {zone.title}
                      </MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText>{formik.touched.zoneId && formik.errors.zoneId}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Nombre */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="nomenclature"
                label="Nombre del Dispositivo"
                value={formik.values.nomenclature || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.nomenclature && Boolean(formik.errors.nomenclature)}
                helperText={formik.touched.nomenclature && formik.errors.nomenclature}
                disabled={formik.isSubmitting || externalLoading}
              />
            </Grid>

            {/* Descripción */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="description"
                label="Descripción"
                value={formik.values.description || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                disabled={formik.isSubmitting || externalLoading}
              />
            </Grid>

            {/* Estado */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Estado</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formik.values.status}
                  onChange={(e) => formik.setFieldValue("status", e.target.value)}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || externalLoading}
                  sx={{ height: 45 }}
                >
                  <MenuItem value={true}>Activo</MenuItem>
                  <MenuItem value={false}>Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Botón */}
            <Grid item xs={12}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={!formik.isValid || formik.isSubmitting || loadingData || externalLoading}
              >
                {formik.isSubmitting || externalLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditMode ? (
                  "Actualizar dispositivo"
                ) : (
                  "Crear dispositivo"
                )}
              </MDButton>
            </Grid>
          </Grid>
        </form>
      </MDBox>
    </Card>
  );
};

FormDevice.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  afterSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    commerceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.bool,
  }),
  isEditMode: PropTypes.bool,
  loading: PropTypes.bool,
};

FormDevice.defaultProps = {
  afterSubmit: () => {},
  initialData: { status: true },
  isEditMode: false,
  loading: false,
};

export default FormDevice;
